import {
  Alert,
  Flex,
  Heading,
  IconButton,
  IconDownloadLine,
  InlineList,
  Text,
  Tooltip,
  View,
} from "@instructure/ui";
import c3 from "c3";
import { t } from "i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ExternalService } from "../../../../../services/ExternalService";
import { TaskTitle } from "../../../../../services/TaskService";
import ChartColors from "../../../../../utils/ChartColors";
import { waitForExpectedResult } from "../../../../../utils/Timer";
import { sort } from "../../../../../utils/utils";
import {
  ERROR,
  useNotification,
  WARNING,
} from "../../../../CustomHooks/NotificationProvider";
import Findings from "./Findings";
import Recommendations from "./Recommendations";

const CanvasJuriedAssessmentChart = ({ measure, filters, triggerMap }) => {
  const showNotification = useNotification();
  const [title, setTitle] = useState("");
  const [loadingChart, setLoadingChart] = useState(true);
  const [error, setError] = useState(false);
  const [noData, setNoData] = useState(false);
  const [graphData, setGraphData] = useState(null);

  const [loadingJaChart, setLoadingJaChart] = useState(true);
  const [jaError, setJaError] = useState(false);
  const [noJaData, setNoJaData] = useState(false);
  const [jaGraphData, setJaGraphData] = useState(null);

  useEffect(() => {
    const getCanvasGraphData = async () => {
      try {
        setLoadingChart(true);
        if (measure.assignment_evidence.length > 0) {
          setTitle(
            filters.masteryResults === "true"
              ? t("assessment.masteryAvg")
              : t("assessment.gradeAvg")
          );
          // filters.mode can either be 'term' (default) or 'course'
          // filters.masteryResults can either be 'true' (default) or 'false'
          const graphData = await ExternalService.getAssessmentResults(
            measure.assignment_evidence,
            filters.mode,
            filters.masteryResults,
            "canvas_course_embedded"
          );

          if (graphData[0].length === 0) {
            setNoData(true);
          } else {
            graphData[0].forEach((term) => {
              for (const key in term) {
                if (key !== "name") {
                  if (term[key] !== null) {
                    term[key] = Number(term[key].toFixed(2));
                  }
                }
              }
            });
            graphData[1] = sort(graphData[1]);
            setGraphData(graphData);
          }
        } else {
          setNoData(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoadingChart(false);
      }
    };
    getCanvasGraphData();
  }, [measure, triggerMap]);

  useEffect(() => {
    const getCanvasJuriedGraphData = async () => {
      try {
        setLoadingJaChart(true);
        if (measure.ja_assignment_evidence.length > 0) {
          setTitle(
            filters.masteryResults === "true"
              ? t("assessment.masteryAvg")
              : t("assessment.gradeAvg")
          );
          // filters.mode can either be 'term' (default) or 'course'
          // filters.masteryResults can either be 'true' (default) or 'false'
          const graphData = await ExternalService.getAssessmentResults(
            measure.ja_assignment_evidence,
            filters.mode,
            filters.masteryResults,
            "juried_assessment"
          );

          if (graphData[0].length === 0) {
            setNoJaData(true);
          } else {
            graphData[0].forEach((term) => {
              for (const key in term) {
                if (key !== "name") {
                  if (term[key] !== null) {
                    term[key] = Number(term[key].toFixed(2));
                  }
                }
              }
            });
            graphData[1] = sort(graphData[1]);
            setJaGraphData(graphData);
          }
        } else {
          setNoJaData(true);
        }
      } catch {
        setJaError(true);
      } finally {
        setLoadingJaChart(false);
      }
    };
    getCanvasJuriedGraphData();
  }, [measure, triggerMap]);

  useEffect(() => {
    if (graphData) {
      c3.generate({
        bindto: document.getElementById("assessment-chart-" + measure.id),
        padding: { bottom: 25, left: 50 },
        color: { pattern: ChartColors },
        legend: { show: false },
        size: { height: 240 },
        data: {
          type: "bar",
          json: graphData[0],
          keys: { x: "name", value: graphData[1] },
        },
        zoom: { enabled: true },
        axis: {
          x: {
            type: "category", // this needed to load string x value
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
          },
        },
        tooltip: {
          order: function (data1, data2) {
            return sort([data1, data2], (d) => d.name);
          },
        },
      });
    }
  }, [graphData, measure.id]);

  useEffect(() => {
    if (jaGraphData) {
      c3.generate({
        bindto: document.getElementById("assessment-chart-ja-" + measure.id),
        padding: { bottom: 25, left: 50 },
        color: { pattern: ChartColors },
        legend: { show: false },
        size: { height: 240 },
        data: {
          type: "bar",
          json: jaGraphData[0],
          keys: { x: "name", value: jaGraphData[1] },
        },
        zoom: { enabled: true },
        axis: {
          x: {
            type: "category", // this needed to load string x value
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
          },
        },
        tooltip: {
          order: function (data1, data2) {
            return sort([data1, data2], (d) => d.name);
          },
        },
      });
    }
  }, [jaGraphData, measure.id]);

  const collectEvidence = (assessmentId) => {
    showNotification(t("notifications.evidence.collectEvidencesInProgress"));
    const task = {
      title: TaskTitle.collectEvidence,
      obj: { assessmentId: assessmentId },
    };

    ExternalService.createTask(task)
      .then((task) => {
        waitForExpectedResult(
          () => ExternalService.getTaskById(task.ddl_id),
          (task) => {
            return task.status === "done" || task.status === "error";
          },
          processCollectEvidenceTask,
          () => {
            showNotification(
              t("notifications.evidence.collectEvidencesError"),
              ERROR
            );
          },
          60,
          3000
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.evidence.collectEvidencesError"),
          ERROR
        );
      });
  };

  const processCollectEvidenceTask = (task) => {
    if (task.status === "error") {
      showNotification(
        t("notifications.evidence.collectEvidencesError"),
        ERROR
      );
    } else if (!task.result || task.result.indexOf("http") !== 0) {
      showNotification(t("notifications.evidence.noEvidenceMessage"), WARNING);
    } else {
      downloadEvidence(task.result);
    }
  };

  const downloadEvidence = (url) => {
    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{t("results.targetedCanvasAssessment")}</Heading>
      </Flex.Item>
      {!!measure.description && (
        <Flex.Item padding="0 0 0 large">
          <Text>{measure.description}</Text>
        </Flex.Item>
      )}
      {loadingChart ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Skeleton style={{ height: 300, borderRadius: 5 }} />
        </Flex.Item>
      ) : error ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="error">
            {t("notifications.general.fetchError", {
              obj: t("results.assessmentResults"),
            })}
          </Alert>
        </Flex.Item>
      ) : noData ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("assessment.noResultsFound")}</Alert>
        </Flex.Item>
      ) : (
        <>
          <Flex.Item size="100%" padding="medium 0 0 large">
            <View textAlign="center">
              <Heading level="h4">{title}</Heading>
              <div
                id={"assessment-chart-" + measure.id}
                style={{ margin: "10px 0 -20px 0", width: "100%" }}
              />
            </View>
          </Flex.Item>
          <Flex.Item size="100%" padding="0 0 medium xx-large">
            <View as="div" textAlign="center">
              <InlineList delimiter="pipe" isUnstyled>
                {graphData[1].map((category, index) => (
                  <InlineList.Item key={index}>
                    <div
                      style={{
                        display: "inline-block",
                        height: 10,
                        width: 10,
                        marginRight: 8,
                        backgroundColor: ChartColors[index],
                      }}
                    />
                    <Text as="span" size="small">
                      {category}
                    </Text>
                  </InlineList.Item>
                ))}
              </InlineList>
            </View>
          </Flex.Item>
        </>
      )}
      <Flex.Item size="100%">
        <Heading level="h3">{t("assessment.canvasJuriedResults")}</Heading>
      </Flex.Item>
      {loadingJaChart ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Skeleton style={{ height: 300, borderRadius: 5 }} />
        </Flex.Item>
      ) : jaError ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="error">
            {t("notifications.general.fetchError", {
              obj: t("results.assessmentResults"),
            })}
          </Alert>
        </Flex.Item>
      ) : noJaData ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("assessment.noResultsFound")}</Alert>
        </Flex.Item>
      ) : (
        <>
          <Flex.Item size="100%" padding="small 0 0 large">
            <View textAlign="center">
              <Heading level="h4">{title}</Heading>
              <div
                id={"assessment-chart-ja-" + measure.id}
                style={{ margin: "10px 0 -20px 0", width: "100%" }}
              />
            </View>
          </Flex.Item>
          <Flex.Item size="100%" padding="0 0 0 xx-large">
            <View as="div" textAlign="center">
              <InlineList delimiter="pipe" isUnstyled>
                {jaGraphData[1].map((category, index) => (
                  <InlineList.Item key={index}>
                    <div
                      style={{
                        display: "inline-block",
                        height: 10,
                        width: 10,
                        marginRight: 8,
                        backgroundColor: ChartColors[index],
                      }}
                    />
                    <Text as="span" size="small">
                      {category}
                    </Text>
                    <Tooltip
                      color="primary-inverse"
                      renderTip={t("evidence.downloadEvidence")}
                      placement="bottom"
                      on={["hover", "focus"]}
                    >
                      <IconButton
                        renderIcon={IconDownloadLine}
                        screenReaderLabel={t("evidence.downloadEvidence")}
                        withBorder={false}
                        withBackground={false}
                        color="primary"
                        size="small"
                        onClick={() => collectEvidence(measure.id)}
                      />
                    </Tooltip>
                  </InlineList.Item>
                ))}
              </InlineList>
            </View>
          </Flex.Item>
        </>
      )}
      <Flex.Item size="100%" padding="0 0 0 large">
        <Findings
          id={measure.id}
          label={<Heading level="h4">{t("juriedAssessment.findings")}</Heading>}
        />
      </Flex.Item>
      <Flex.Item size="100%" padding="0 0 0 large">
        <Recommendations
          id={measure.id}
          label={
            <Heading level="h4">
              {t("juriedAssessment.recommendations")}
            </Heading>
          }
        />
      </Flex.Item>
    </Flex>
  );
};

export default CanvasJuriedAssessmentChart;
